import * as Page from '../../../components/content/page-header';
import * as Button from '../../../components/button/button';
import { Icons } from '../../../components/icons/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaginatedTable } from '../../../components/table/paginated-table';
import { MainContent } from '../../../components/layout/content';
import usePageState from '../../../hooks/use-page-state';
import { Modes } from '../../../types/page-modes';
import { Actions, Content, Dialog, Title } from '../../../components/dialog/dialog';
import React, { useMemo, useState } from 'react';
import queryString from 'query-string';
import { Pageable, Query, usePagination } from '../../../hooks/use-pagination';
import {
  ActionColumn,
  CellRenderer,
  GearIconColumn,
  ManuallyAddedColumn
} from '../../../components/table/columns';
import { useDeleteMutation } from '../../../hooks/use-mutations';
import { MapServer } from '../types/map-server';
import { NotInSync } from '../../../components/not-in-sync/not-in-sync';
import { RevertToMaster2 } from '../../../components/revert-to-master/revert-to-master-2';
import { MapService } from '../types/map-service';
import { KeyValue } from '../../../types/key-value';
import { RecordStatus } from '../../../types/record-status';
import tenantService from '../../../services/tenant-service';
import { MapLayer, MapLayerKey } from '../types/map-layer';
import { SearchField, useSearch } from '../../../hooks/use-search';

export const MapLayers = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { mapServiceId } = queryString.parse(search);
  const [query, setQuery] = useState<Query>({});
  const { isSearching, searchData, setSearchQuery } = useSearch<MapLayer>({ path: '/map-layers/search' });
  const { data, isLoading } = usePagination<Pageable<MapLayer>>(mapServiceId ? `/map-services/${mapServiceId as string}/map-layers` : '/map-layers', query);
  const deleteMutation = useDeleteMutation();
  const mapServices = usePagination<Pageable<MapService>>('/map-services', { size: 9999 });
  const mapServiceData = mapServices.data?.content || [];
  const mapServiceMap = useMemo(() => {
    const map: KeyValue = {};
    mapServiceData?.forEach((item): void => {
      map[item.id as string] = item.slug as string;
    });
    return map;
  }, [mapServiceData]);

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/map-layers/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const columns = [
    ...GearIconColumn(),
    { accessor: 'name', Header: 'Name', sort: true, Cell: CellRenderer },
    { accessor: 'type', Header: 'Type', sort: true, Cell: CellRenderer },
    { accessor: 'mapServiceId', Header: 'Map Service', sort: true, Cell: (item: any): JSX.Element => {
      const row = item.row.original as MapLayer;
      const className = item.row.original.recordStatus === RecordStatus.GM_LOCKED && !tenantService.isGoldMaster() ? 'locked-column' : '';
      return <span className={className}>{mapServiceMap[row.mapServiceId as MapLayerKey]}</span>;
    } },
    ManuallyAddedColumn(),
    ActionColumn({
      onDelete: (item: MapServer): void => setPageState(Modes.DELETE, item),
      onUpdate: (item: MapServer): void => navigate(`/map-layers/${item.id}`)
    })
  ];

  return <>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>Map Layers <NotInSync path="map-layers" /></Page.Title>
      <Page.SubTitle>List of available map layers.</Page.SubTitle>
      <Page.Actions>
        {!mapServiceId ? <SearchField /> : <span />}
        <div>
          <RevertToMaster2 path="map-layers" />
          <Button.LightBlackSmall onClick={(): void => navigate(`${pathname}/import`)}>
            <Icons.Import />
            Import
          </Button.LightBlackSmall>
          <Button.PrimarySmall onClick={(): void => navigate(`${pathname}/new`)}>
            <Icons.Add />
            Add Map Layer
          </Button.PrimarySmall>
        </div>
      </Page.Actions>
    </Page.Header>
    <MainContent>
      {isSearching
        ? <PaginatedTable isLoading={isLoading} columns={columns} data={searchData} onChange={setSearchQuery} />
        : <PaginatedTable isLoading={isLoading} columns={columns} data={data} onChange={setQuery} />
      }
    </MainContent>
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Map Layer</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};
